import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, ChevronDown, Menu, X, Cloud, Sparkles } from "lucide-react";
import { PANELS } from "@/data/megaMenuPanels";
import LangToggle from "@/components/landing/LangToggle";

// Smooth-scroll to hash anchors with header offset
function smoothScrollTo(hash: string) {
  const id = hash.replace(/^#/, "");
  const el = document.getElementById(id);
  if (!el) return false;
  const y = el.getBoundingClientRect().top + window.scrollY - 88;
  window.scrollTo({ top: y, behavior: "smooth" });
  history.replaceState(null, "", `#${id}`);
  return true;
}

function SmartLink({
  href,
  className,
  onClick,
  children,
}: {
  href: string;
  className?: string;
  onClick?: () => void;
  children: React.ReactNode;
}) {
  if (href.startsWith("#")) {
    return (
      <a
        href={href}
        className={className}
        onClick={(e) => {
          if (smoothScrollTo(href)) e.preventDefault();
          onClick?.();
        }}
      >
        {children}
      </a>
    );
  }
  return (
    <Link to={href} className={className} onClick={onClick}>
      {children}
    </Link>
  );
}

// Top-level route links shown in the nav
const QUICK_LINKS: { href: string; label: string }[] = [
  { href: "/features", label: "Features" },
  { href: "/stories", label: "Stories" },
  { href: "/pricing", label: "Pricing" },
  { href: "/faq", label: "FAQ" },
];

export default function MegaMenu({
  logo,
  ctaLabel = "Start free",
  signInLabel = "Sign in",
  right,
}: {
  logo: React.ReactNode;
  ctaLabel?: string;
  signInLabel?: string;
  right?: React.ReactNode;
}) {
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobilePanel, setMobilePanel] = useState<string | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const [progress, setProgress] = useState(0);
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<number | null>(null);
  const location = useLocation();

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpenKey(null);
    };
    const onEsc = (e: KeyboardEvent) => e.key === "Escape" && (setOpenKey(null), setMobileOpen(false));
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, []);

  // Sticky shrink + top progress bar
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 24);
      const h = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(h > 0 ? Math.min(100, (y / h) * 100) : 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Active section highlighting via IntersectionObserver
  useEffect(() => {
    const ids = QUICK_LINKS.map((l) => l.href.slice(1));
    const els = ids
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => !!el);
    if (!els.length) return;
    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) setActiveSection(visible[0].target.id);
      },
      { rootMargin: "-40% 0px -50% 0px", threshold: [0, 0.25, 0.5, 1] },
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [location.pathname]);

  const scheduleClose = () => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setOpenKey(null), 120);
  };
  const cancelClose = () => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
  };

  const activePanel = PANELS.find((p) => p.key === openKey);

  return (
    <div ref={wrapRef} className="relative z-50">
      {/* Top scroll progress bar */}
      <div
        aria-hidden
        className="fixed top-0 left-0 right-0 h-[3px] z-[60] pointer-events-none"
      >
        <div
          className="h-full bg-gradient-to-r from-[#1ba179] via-[#25c39a] to-[#4de3b8] transition-[width] duration-150 ease-out shadow-[0_0_12px_rgba(37,195,154,0.55)]"
          style={{ width: `${progress}%` }}
        />
      </div>

      <nav
        className={`max-w-7xl mx-auto flex items-center justify-between pl-4 pr-2 sm:pl-5 sm:pr-2 bg-white/80 backdrop-blur-xl border rounded-full transition-all duration-300 ${
          scrolled
            ? "h-12 border-[#e2e8f0]/90 shadow-[0_14px_40px_-16px_rgba(15,23,42,0.22),0_2px_6px_rgba(15,23,42,0.06)] bg-white/90"
            : "h-14 border-[#e2e8f0] shadow-[0_8px_30px_-15px_rgba(37,195,154,0.15),0_1px_2px_rgba(15,23,42,0.04)]"
        }`}
        onMouseLeave={scheduleClose}
      >
        <div className="flex items-center gap-6">
          {logo}
          <ul className="hidden lg:flex items-center gap-0.5 text-[13px] font-medium text-[#334155]">
            {PANELS.map((p) => (
              <li key={p.key}>
                <button
                  onMouseEnter={() => {
                    cancelClose();
                    setOpenKey(p.key);
                  }}
                  onFocus={() => setOpenKey(p.key)}
                  onClick={() => setOpenKey(openKey === p.key ? null : p.key)}
                  aria-expanded={openKey === p.key}
                  className={`inline-flex items-center gap-1 px-3 h-9 rounded-full transition ${
                    openKey === p.key ? "bg-[#f1f5f9] text-[#0f172a]" : "hover:text-[#0f172a]"
                  }`}
                >
                  {p.label}
                  <ChevronDown
                    className={`w-3.5 h-3.5 transition-transform ${openKey === p.key ? "rotate-180" : ""}`}
                  />
                </button>
              </li>
            ))}
            <li className="mx-1 h-5 w-px bg-[#e2e8f0]" aria-hidden />
            {QUICK_LINKS.map((l) => {
              const isActive = location.pathname === l.href;
              return (
                <li key={l.href}>
                  <Link
                    to={l.href}
                    className={`relative inline-flex items-center px-3 h-9 rounded-full transition ${
                      isActive
                        ? "text-[#0f172a] bg-[#e6faf3]"
                        : "hover:text-[#0f172a] hover:bg-[#f8fafc]"
                    }`}
                  >
                    {l.label}
                    {isActive && (
                      <span
                        aria-hidden
                        className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-[#1ba179]"
                      />
                    )}
                  </Link>
                </li>
              );
            })}
            <li>
              <Link to="/about" className="inline-flex items-center px-3 h-9 rounded-full hover:text-[#0f172a] hover:bg-[#f8fafc] transition">
                About
              </Link>
            </li>
          </ul>
        </div>


        <div className="flex items-center gap-2 sm:gap-3">
          {right ?? <LangToggle />}

          <Link
            to="/auth"
            className="hidden sm:inline text-[13px] font-semibold text-[#0f172a] px-3 py-2 hover:bg-[#f8fafc] rounded-full transition"
          >
            {signInLabel}
          </Link>
          <Link
            to="/auth"
            className="group bg-gradient-to-br from-[#1ba179] to-[#25c39a] hover:from-[#0f8f6b] hover:to-[#1ba179] text-white text-[13px] font-semibold pl-4 pr-1.5 py-1.5 rounded-full transition-all inline-flex items-center gap-2 shadow-[0_6px_18px_-6px_rgba(37,195,154,0.55)]"
          >
            {ctaLabel}
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-white/15 group-hover:bg-white/25 transition">
              <ArrowRight className="w-3 h-3" />
            </span>
          </Link>
          <button
            className="lg:hidden inline-flex items-center justify-center w-9 h-9 rounded-full text-[#1ba179] bg-[#e6faf3] border border-[#c7f1e2] hover:bg-[#c7f1e2] hover:text-[#0f8f6b] hover:border-[#9ce6cd] active:scale-95 transition outline-none focus-visible:ring-2 focus-visible:ring-[#1ba179] focus-visible:ring-offset-2 focus-visible:ring-offset-white"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <X className="w-4 h-4" strokeWidth={2.4} /> : <Menu className="w-4 h-4" strokeWidth={2.4} />}
          </button>
        </div>
      </nav>

      {/* Desktop mega panel */}
      {activePanel && (
        <div
          className="hidden lg:block absolute left-1/2 -translate-x-1/2 top-[calc(100%+10px)] w-[min(1120px,calc(100vw-2rem))]"
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
        >
          <div className="rounded-3xl border border-[#e2e8f0] bg-white shadow-[0_24px_60px_-20px_rgba(15,23,42,0.18)] overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150">
            <div className="grid grid-cols-12">
              <div className={`${activePanel.featured ? "col-span-9" : "col-span-12"} p-6 grid grid-cols-1 md:grid-cols-3 gap-6`}>
                {activePanel.columns.map((col) => (
                  <div key={col.heading}>
                    <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#94a3b8] mb-3">
                      {col.heading}
                    </p>
                    <ul className="space-y-1">
                      {col.items.map((it) => (
                        <li key={it.title}>
                          <SmartLink
                            href={it.href}
                            className="group flex items-start gap-3 rounded-xl p-2.5 -mx-2.5 hover:bg-[#f8fafc] transition"
                            onClick={() => setOpenKey(null)}
                          >
                            <span className="mt-0.5 inline-flex items-center justify-center w-9 h-9 rounded-lg bg-[#f1f5f9] text-[#1ba179] group-hover:bg-[#1ba179] group-hover:text-white transition">
                              <it.icon className="w-[18px] h-[18px]" />
                            </span>
                            <span className="min-w-0">
                              <span className="flex items-center gap-2">
                                <span className="text-[13.5px] font-semibold text-[#0f172a] leading-tight">
                                  {it.title}
                                </span>
                                {it.badge && (
                                  <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#1ba179]/10 text-[#1ba179]">
                                    {it.badge}
                                  </span>
                                )}
                              </span>
                              <span className="block text-[12px] text-[#64748b] leading-snug mt-0.5">
                                {it.desc}
                              </span>
                            </span>
                          </SmartLink>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
              {activePanel.featured && (
                <div className="col-span-3 border-l border-[#e2e8f0] bg-gradient-to-br from-[#f8fafc] to-white p-6 flex flex-col justify-between">
                  <div>
                    <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-[#1ba179] mb-3">
                      <Sparkles className="w-3 h-3" /> Featured
                    </div>
                    <h4 className="text-[15px] font-bold text-[#0f172a] leading-snug">
                      {activePanel.featured.title}
                    </h4>
                    <p className="text-[12.5px] text-[#64748b] mt-1.5 leading-relaxed">
                      {activePanel.featured.desc}
                    </p>
                  </div>
                  <SmartLink
                    href={activePanel.featured.href}
                    onClick={() => setOpenKey(null)}
                    className="mt-6 inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[#0f172a] hover:text-[#1ba179] transition"
                  >
                    {activePanel.featured.cta}
                    <ArrowRight className="w-3.5 h-3.5" />
                  </SmartLink>
                </div>
              )}
            </div>
            <div className="border-t border-[#e2e8f0] bg-[#fafafa] px-6 py-3 flex items-center justify-between text-[12px] text-[#64748b]">
              <span className="inline-flex items-center gap-2">
                <Cloud className="w-3.5 h-3.5" /> Hosted & encrypted in-region
              </span>
              <Link to="/auth" className="font-semibold text-[#0f172a] hover:text-[#1ba179] transition inline-flex items-center gap-1">
                Explore all features <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden mt-2 rounded-3xl border border-[#e2e8f0] bg-white shadow-[0_20px_60px_-20px_rgba(15,23,42,0.15)] overflow-hidden">
          <div className="p-2">
            {PANELS.map((p) => (
              <div key={p.key} className="border-b border-[#f1f5f9] last:border-b-0">
                <button
                  onClick={() => setMobilePanel(mobilePanel === p.key ? null : p.key)}
                  className="w-full flex items-center justify-between px-3 py-3 text-[14px] font-semibold text-[#0f172a]"
                >
                  {p.label}
                  <ChevronDown className={`w-4 h-4 transition-transform ${mobilePanel === p.key ? "rotate-180" : ""}`} />
                </button>
                {mobilePanel === p.key && (
                  <div className="pb-3 space-y-4">
                    {p.columns.map((col) => (
                      <div key={col.heading} className="px-3">
                        <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#94a3b8] mb-2">
                          {col.heading}
                        </p>
                        <ul className="space-y-1">
                          {col.items.map((it) => (
                            <li key={it.title}>
                              <SmartLink
                                href={it.href}
                                onClick={() => setMobileOpen(false)}
                                className="flex items-start gap-3 rounded-xl p-2 hover:bg-[#f8fafc] transition"
                              >
                                <span className="mt-0.5 inline-flex items-center justify-center w-8 h-8 rounded-lg bg-[#f1f5f9] text-[#1ba179]">
                                  <it.icon className="w-4 h-4" />
                                </span>
                                <span>
                                  <span className="block text-[13px] font-semibold text-[#0f172a]">{it.title}</span>
                                  <span className="block text-[11.5px] text-[#64748b]">{it.desc}</span>
                                </span>
                              </SmartLink>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div className="grid grid-cols-2 gap-1 p-1">
              {QUICK_LINKS.map((l) => (
                <Link
                  key={l.href}
                  to={l.href}
                  onClick={() => setMobileOpen(false)}
                  className="px-3 py-2.5 text-[13px] font-semibold text-[#0f172a] rounded-xl hover:bg-[#f1f5f9] transition"
                >
                  {l.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
